import { CleaningFrequency, DayOfWeek, } from '../organization/OrganizationEntity';
export const COMPLIANCE_INACTIVITY_DAYS = 30;
export const COMPLIANCE_INACTIVITY_SNOOZE_DAYS = 7;
export const COMPLIANCE_SCHEDULE_TIMEZONE = 'Europe/Madrid';
export const ALL_OPENING_DAYS = [
    DayOfWeek.MONDAY,
    DayOfWeek.TUESDAY,
    DayOfWeek.WEDNESDAY,
    DayOfWeek.THURSDAY,
    DayOfWeek.FRIDAY,
    DayOfWeek.SATURDAY,
    DayOfWeek.SUNDAY,
];
export const getFrequencyLabel = (frequency) => {
    switch (frequency) {
        case CleaningFrequency.DAILY:
            return 'Diario';
        case CleaningFrequency.WEEKLY:
            return 'Semanal';
        case CleaningFrequency.MONTHLY:
            return 'Mensual';
        default:
            return frequency;
    }
};
export const isComplianceActive = (compliance) => {
    if (!compliance)
        return false;
    const hasCleaningZones = compliance.cleaningZones.length > 0;
    const hasWorkers = compliance.workers.length > 0;
    const hasTemperatureZones = compliance.temperatureZones.length > 0;
    return hasWorkers && (hasCleaningZones || hasTemperatureZones);
};
/** Missing `complianceEnabled` means enabled (backward compatible). */
export const isComplianceEnabled = (complianceEnabled) => complianceEnabled !== false;
/** Normalize boolean flags that may have been stored as strings in emulators/manual edits. */
export const normalizeComplianceEnabled = (value) => {
    if (value === false || value === 'false') {
        return false;
    }
    if (value === true || value === 'true') {
        return true;
    }
    if (value === undefined || value === null || value === '') {
        return undefined;
    }
    return undefined;
};
export const hasOrganizationComplianceSetup = (organization) => !!organization.restaurants?.some(restaurant => isComplianceActive(restaurant.compliance));
export const resolveOrganizationComplianceActive = (organization) => isComplianceEnabled(organization.complianceEnabled) && hasOrganizationComplianceSetup(organization);
/**
 * Safe derivation for organizationConverter partial merges.
 * `complianceEnabled: false` always wins.
 * When restaurants are written without `complianceEnabled`, honor an explicit
 * `isComplianceActive` from the caller; otherwise omit so merge keeps Firestore
 * (avoids reviving the scheduler after disable).
 */
export const resolveIsComplianceActiveForWrite = (organization) => {
    if (organization.complianceEnabled === false) {
        return false;
    }
    if (organization.restaurants !== undefined) {
        if (organization.complianceEnabled == null) {
            return organization.isComplianceActive;
        }
        return resolveOrganizationComplianceActive({
            complianceEnabled: organization.complianceEnabled,
            restaurants: organization.restaurants,
        });
    }
    if (organization.complianceEnabled !== undefined) {
        return organization.isComplianceActive === true;
    }
    return organization.isComplianceActive;
};
export const isComplianceUsageInactive = ({ now, lastConfirmedAt, complianceEnabledAt, inactivityDays = COMPLIANCE_INACTIVITY_DAYS, }) => {
    const cutoffMs = now.getTime() - inactivityDays * 24 * 60 * 60 * 1000;
    if (complianceEnabledAt && complianceEnabledAt.getTime() > cutoffMs) {
        return false;
    }
    if (!lastConfirmedAt) {
        // Without an enable timestamp we cannot know whether a month has passed.
        return !!complianceEnabledAt;
    }
    return lastConfirmedAt.getTime() <= cutoffMs;
};
export const resolveLatestConfirmedAt = (temperatureConfirmedAt, cleaningConfirmedAt) => {
    const temperatureMs = temperatureConfirmedAt ? temperatureConfirmedAt.toMillis() : null;
    const cleaningMs = cleaningConfirmedAt ? cleaningConfirmedAt.toMillis() : null;
    if (temperatureMs === null && cleaningMs === null) {
        return null;
    }
    if (temperatureMs === null) {
        return new Date(cleaningMs);
    }
    if (cleaningMs === null) {
        return new Date(temperatureMs);
    }
    return new Date(Math.max(temperatureMs, cleaningMs));
};
export const resolveOpeningDays = (openingDays) => openingDays === undefined ? ALL_OPENING_DAYS : openingDays;
export const getDayOfWeekInTimeZone = (date, timeZone = COMPLIANCE_SCHEDULE_TIMEZONE) => {
    const weekday = new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'long' })
        .format(date)
        .toLowerCase();
    if (!ALL_OPENING_DAYS.includes(weekday)) {
        throw new Error(`Unexpected weekday "${weekday}" for timezone ${timeZone}`);
    }
    return weekday;
};
export const isRestaurantOpenOnDate = (restaurant, date = new Date(), timeZone = COMPLIANCE_SCHEDULE_TIMEZONE) => {
    const openingDays = resolveOpeningDays(restaurant.compliance?.openingDays);
    const dayOfWeek = getDayOfWeekInTimeZone(date, timeZone);
    return openingDays.includes(dayOfWeek);
};
//# sourceMappingURL=ComplianceService.js.map